import { useEffect, useMemo, useState } from 'react';
import { Check, Lock, Search, X } from 'lucide-react';
import { Input } from '../ui/input';
import { creditPreview } from '../../lib/aiStudioApi';
import { cn } from '../../lib/utils';

function TierPicker({ tiers, value, onChange }) {
  const [cost, setCost] = useState({});
  useEffect(() => {
    tiers.forEach((t) => creditPreview(5, t.resolution, t.id === 'premium' ? 'gateway' : 'budget').then((r) => setCost((c) => ({ ...c, [t.id]: r.credits }))).catch(() => {}));
  }, [tiers]);
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-3" data-testid="quality-tier-picker">
      {tiers.map((t) => (
        <button key={t.id} type="button" onClick={() => onChange(t.id)} data-testid={`quality-tier-${t.id}`}
          className={cn('rounded-xl border p-3 text-left transition-colors duration-200',
            value === t.id ? 'border-amber-400 bg-amber-500/10' : 'border-neutral-800 bg-neutral-900/40 hover:border-neutral-600')}>
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-neutral-100">{t.label}</span>
            <span className="rounded bg-neutral-800 px-1.5 py-0.5 font-mono text-[10px] text-neutral-300">{t.resolution}</span>
          </div>
          <div className="mt-0.5 text-[11px] text-neutral-500">{t.blurb}</div>
          {cost[t.id] !== undefined && <div className="mt-1.5 font-mono text-[11px] text-amber-300" data-testid={`quality-tier-${t.id}-credits`}>≈ {cost[t.id]} cr per {t.id === 'premium' ? '5s' : 'animated'} shot</div>}
        </button>
      ))}
    </div>
  );
}

function StyleCard({ style, selected, onPick }) {
  return (
    <button type="button" onClick={() => onPick(style.id)} data-testid={`render-style-${style.id}`} aria-pressed={selected}
      className={cn('group relative overflow-hidden rounded-xl border bg-neutral-900 text-left transition-[border-color,transform] duration-200 hover:-translate-y-0.5',
        selected ? 'border-amber-400 ring-2 ring-amber-400/40' : 'border-neutral-800 hover:border-neutral-600')}>
      <div className="aspect-[4/3] overflow-hidden">
        <img src={style.image} alt={style.label} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
      </div>
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black via-black/80 to-transparent p-2.5 pt-8">
        <div className="text-[13px] font-semibold leading-tight text-white">{style.label}</div>
        <div className="mt-0.5 line-clamp-1 text-[11px] text-neutral-300">{style.blurb}</div>
      </div>
      <span className="absolute left-2 top-2 rounded bg-black/60 px-1.5 py-0.5 font-mono text-[9px] text-neutral-200 backdrop-blur">{style.frame_rate.split(' (')[0]}</span>
      {selected && <span className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-amber-500 text-neutral-900"><Check size={14} /></span>}
    </button>
  );
}

/** Step 2 — Render Style gallery (style lock) + quality tier. */
export function RenderStyleGallery({ project, catalog, onPatch }) {
  const [group, setGroup] = useState('All');
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState(project.render_style || '');
  useEffect(() => setPicked(project.render_style || ''), [project.render_style]);
  const styles = useMemo(() => catalog.render_styles || [], [catalog.render_styles]);
  const groups = catalog.render_style_groups || [];
  const shown = useMemo(() => styles.filter((s) => (group === 'All' || s.group === group)
    && `${s.label} ${s.blurb}`.toLowerCase().includes(q.trim().toLowerCase())), [styles, group, q]);
  const current = styles.find((s) => s.id === picked);
  const pick = (id) => { setPicked(id); onPatch({ render_style: id }); };

  return (
    <div className="space-y-5" data-testid="render-style-gallery">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-neutral-800 bg-neutral-900/40 p-4">
        <div>
          <div className="text-sm font-semibold text-neutral-100">Pick one look for the whole production</div>
          <div className="text-xs text-neutral-500">Every storyboard still and motion shot is locked to this style, so nothing drifts between shots.</div>
        </div>
        <div className={cn('flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs', current ? 'border-amber-400/50 bg-amber-500/10 text-amber-100' : 'border-neutral-700 text-neutral-400')} data-testid="render-style-lock-chip">
          <Lock size={12} /> {current ? `Style lock · ${current.label}` : 'No style lock yet'}
          {current && <button type="button" onClick={() => pick('')} className="ml-1 text-neutral-400 hover:text-white" aria-label="Clear style" data-testid="render-style-clear"><X size={12} /></button>}
        </div>
      </div>

      <TierPicker tiers={catalog.quality_tiers || []} value={project.quality_tier || 'standard'} onChange={(quality_tier) => onPatch({ quality_tier })} />

      <div className="flex flex-wrap items-center gap-2">
        {['All', ...groups].map((g) => (
          <button key={g} type="button" onClick={() => setGroup(g)} data-testid={`render-style-group-${g.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            className={cn('rounded-full px-3 py-1 text-xs transition-colors duration-200', group === g ? 'bg-amber-500 text-neutral-900' : 'bg-neutral-900 text-neutral-400 hover:text-neutral-100')}>
            {g} <span className="opacity-60">{g === 'All' ? styles.length : styles.filter((s) => s.group === g).length}</span>
          </button>
        ))}
        <div className="relative ml-auto w-full sm:w-56">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search styles" className="h-8 border-neutral-800 bg-neutral-900 pl-8 text-xs text-neutral-100" data-testid="render-style-search" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5" data-testid="render-style-grid">
        {shown.map((s) => <StyleCard key={s.id} style={s} selected={picked === s.id} onPick={pick} />)}
        {!shown.length && <div className="col-span-full rounded-xl border border-dashed border-neutral-800 p-8 text-center text-sm text-neutral-500">No style matches “{q}”.</div>}
      </div>
    </div>
  );
}
